import request from '#/utils/request';
import { withFmsActor } from '#/api/fmsMgmt/fmsActorConfig';
import { keysToSnake } from '#/api/adapters/wireCase';
import { parseVer4DetailResponse } from '#/api/adapters/envelope';

/**
 * 採購付款（應付）金流紀錄。`accountId` 取自兩種採購單 `GET …/payment-plan` 的 `account_id`。
 * @see backend-docs docs/requirements/srm/purchase-order-payment.md「財務付款 route」
 *
 * 紀錄形狀（test-cyt 讀回）：`record_type` PAYMENT／REFUND、`status` DRAFT／CONFIRMED／VOIDED、
 * `amount`、`actual_paid_at`（`YYYY-MM-DD HH:mm:ss`）、`remark`、`allocations[{ phase_id, amount }]`。
 */
const basePath = (accountId) => `/fms/purchase-payments/${accountId}/records`;

function withIdempotencyKey(idempotencyKey) {
  return withFmsActor({ headers: { 'Idempotency-Key': idempotencyKey } });
}

/** GET `…/records`（`fms.purchase_payment.read`）：回 `data` 陣列。 */
export async function fetchPurchasePaymentRecords(accountId) {
  const { data: raw } = await request.get(basePath(accountId));
  const parsed = parseVer4DetailResponse(raw);
  return { ...parsed, data: Array.isArray(parsed.data) ? parsed.data : [] };
}

/** POST `…/records`：建立付款草稿；`confirm` 為 true 時同次確認（需帶 Idempotency-Key）。 */
export async function createPurchasePaymentRecord(accountId, payload, { idempotencyKey } = {}) {
  const { data: raw } = await request.post(
    basePath(accountId),
    keysToSnake(payload),
    idempotencyKey ? withIdempotencyKey(idempotencyKey) : withFmsActor(),
  );
  return parseVer4DetailResponse(raw);
}

/**
 * POST `…/records/with-attachments`（multipart）：同次建立付款、上傳憑證並選擇確認。
 * JSON 欄位放在 `data` part，檔案逐一 append 為 `files`。
 */
export async function createPurchasePaymentRecordWithAttachments(
  accountId,
  payload,
  files,
  { idempotencyKey } = {},
) {
  const formData = new FormData();
  formData.append(
    'data',
    new Blob([JSON.stringify(keysToSnake(payload))], { type: 'application/json' }),
  );
  files.forEach((file) => formData.append('files', file));
  const config = idempotencyKey ? withIdempotencyKey(idempotencyKey) : withFmsActor();
  const { data: raw } = await request.post(`${basePath(accountId)}/with-attachments`, formData, {
    ...config,
    headers: { ...config.headers, 'Content-Type': undefined },
  });
  return parseVer4DetailResponse(raw);
}

/** PATCH `…/records/{recordId}`：僅未確認草稿。 */
export async function updatePurchasePaymentRecord(accountId, recordId, payload) {
  const { data: raw } = await request.patch(
    `${basePath(accountId)}/${recordId}`,
    keysToSnake(payload),
    withFmsActor(),
  );
  return parseVer4DetailResponse(raw);
}

/** POST `…/records/{recordId}/confirm`（`fms.purchase_payment.confirm`）：必須帶 Idempotency-Key。 */
export async function confirmPurchasePaymentRecord(accountId, recordId, { idempotencyKey }) {
  const { data: raw } = await request.post(
    `${basePath(accountId)}/${recordId}/confirm`,
    {},
    withIdempotencyKey(idempotencyKey),
  );
  return parseVer4DetailResponse(raw);
}

/** POST `…/records/{recordId}/void`（`fms.purchase_payment.void`）：沖銷已確認紀錄，body `reason`。 */
export async function voidPurchasePaymentRecord(accountId, recordId, payload) {
  const { data: raw } = await request.post(
    `${basePath(accountId)}/${recordId}/void`,
    keysToSnake(payload),
    withFmsActor(),
  );
  return parseVer4DetailResponse(raw);
}

/** POST `…/records/refunds`（`fms.purchase_payment.refund`）：登錄供應商實際退款，必須帶 Idempotency-Key。 */
export async function createPurchasePaymentRefund(accountId, payload, { idempotencyKey }) {
  const { data: raw } = await request.post(
    `${basePath(accountId)}/refunds`,
    keysToSnake(payload),
    withIdempotencyKey(idempotencyKey),
  );
  return parseVer4DetailResponse(raw);
}

/** POST `…/records/reconcile-history`（`fms.purchase_payment.confirm`）：`{ remark }` 核對依據，未知→已知。 */
export async function reconcilePurchasePaymentHistory(accountId, { remark }) {
  const { data: raw } = await request.post(
    `${basePath(accountId)}/reconcile-history`,
    { remark },
    withFmsActor(),
  );
  return parseVer4DetailResponse(raw);
}
